// Write operations against the API. Every state change sends the version the
// user saw (expectedVersion); a 409 means "reload, don't overwrite".
import type { DocumentTypeContract } from '@rimon/contracts';
import { LIVE_EVENTS, LIVE_SESSION_INVALID, PAY_ASSISTANT_EVENTS as EV } from '@rimon/contracts';
import { io, type Socket } from 'socket.io-client';
import { api, apiBytes, API_URL, tokens } from '../api/client';
import { saveUrl, ZIP_DIR } from '../desktop/files';
import { recheckSession } from './sessionCheck';
import type { components } from '../api/schema';
import type { ApiClosing, ApiCompany, ApiDocument } from './compta';
import type { Layer } from '@rimocore/shared/overlay/overlay';

type UpdateCompany = components['schemas']['UpdateCompanyDto'] & Partial<Record<'name' | 'address' | 'postalCode' | 'city' | 'country' | 'rcs' | 'vatNumber' | 'phone' | 'email' | 'website', string>>;

/** POST compta/documents/:id/payments — amount as decimal string, paidAt defaults to now on the server. */
export function markPaid(doc: { id: string; version: number }, p: { amount: string; bank: 'BIL' | 'BGL'; communication?: string }) {
  return api<{ id: string; document: { id: string; version: number } }>('/compta/documents/' + doc.id + '/payments', {
    method: 'POST',
    body: { bank: p.bank, amount: p.amount, expectedVersion: doc.version, ...(p.communication ? { communication: p.communication } : {}) },
  });
}

/** DELETE compta/payments/:id — takes a payment back (document returns to PRINTED); 409 payment_reconciled once matched. */
export const revertPayment = (paymentId: string, expectedVersion: number) =>
  api<{ document: { id: string; version: number; status: string } }>('/compta/payments/' + paymentId, { method: 'DELETE', query: { expectedVersion } });

export interface PreparationBatch {
  id: string;
  status: 'DRAFT' | 'PROCESSING' | 'EXPORTED' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  exportFileId: string | null;
  exportFile: { id: string; originalFilename: string; sizeBytes: string } | null;
  items: Array<{ documentId: string; document: ApiDocument }>;
}

export const createPreparation = (documentIds: string[]) =>
  api<PreparationBatch>('/compta/preparation', { method: 'POST', body: { documentIds } });
export const getPreparation = (id: string) => api<PreparationBatch>('/compta/preparation/' + id);
/** „Vorbereitung abschließen“: the server builds the ZIP now (then poll with waitForExport). */
export const completePreparation = (id: string) => api<PreparationBatch>('/compta/preparation/' + id + '/complete', { method: 'POST' });
/** Cancels in DRAFT, PROCESSING or FAILED: `restored` invoices are back to their previous status (409 preparation_completed once exported). */
export const cancelPreparation = (id: string) => api<PreparationBatch & { restored: string[]; skipped: Array<{ documentId: string; status: string }> }>('/compta/preparation/' + id + '/cancel', { method: 'POST' });
export const listPreparations = (status?: string) => api<Array<{ id: string; status: PreparationBatch['status'] }>>('/compta/preparation', { query: { status } });
/** Signed download of the finished ZIP (409 export_not_ready until COMPLETED). */
export async function downloadExport(batchId: string, name?: 'date' | 'month'): Promise<void> {
  const r = await api<{ url: string; filename: string }>('/compta/preparation/' + batchId + '/export', { query: name ? { name } : {} });
  // Desktop: straight into the ZIP folder of the preferences.
  await saveUrl(r.url, r.filename, { dir: ZIP_DIR() });
}

/** Polls a preparation batch until the export worker is done (or gives up). */
export async function waitForExport(id: string, timeoutMs = 60_000): Promise<PreparationBatch> {
  const end = Date.now() + timeoutMs;
  for (;;) {
    const b = await getPreparation(id);
    if (b.status !== 'PROCESSING' && b.status !== 'DRAFT') return b;
    if (Date.now() > end) return b;
    await new Promise((r) => setTimeout(r, 500));
  }
}

export const allocateCredit = (creditNoteId: string, invoiceId: string, allocatedAmount: string) =>
  api('/compta/documents/credit-notes/allocate', { method: 'POST', body: { creditNoteId, invoiceId, allocatedAmount } });

export const transition = (doc: { id: string; version: number }, to: ApiDocument['status'], reason?: string) =>
  api<ApiDocument>('/compta/documents/' + doc.id + '/status', { method: 'POST', body: { to, expectedVersion: doc.version, ...(reason ? { reason } : {}) } });

// ---------- files ----------

/** Short-lived signed URL — use immediately, never store. */
export const signedUrl = (fileId: string) => api<{ url: string; expiresIn: number }>('/core/files/' + fileId + '/download').then((r) => r.url);

/** Triggers a browser download of a stored file (ZIP export, PDF original). */
export async function downloadFile(fileId: string, filename = 'Dokument.pdf'): Promise<void> {
  await saveUrl(await signedUrl(fileId), filename);
}

/** First-page tile previews for up to 100 documents; `url` is null while the server still renders it. */
export const thumbnails = (documentIds: string[]) =>
  api<{ documentId: string; url: string | null; expiresIn: number }[]>('/compta/documents/thumbnails', { query: { ids: documentIds.join(',') } });

/** One document (context menu on rows whose document is not in the live store, e.g. the Réception list). */
export const getDocument = (documentId: string) => api<ApiDocument>('/compta/documents/' + documentId);

/** Customers (OUTGOING_INVOICE party picker). */
export const listCustomers = () => api<Array<{ id: string; name: string }>>('/core/customers');

/** PATCH compta/documents/:id — manual correction („Bearbeiten …“); only the fields sent change, null clears. */
export const updateDocument = (documentId: string, body: Record<string, unknown> & { expectedVersion: number }) =>
  api<ApiDocument>('/compta/documents/' + documentId, { method: 'PATCH', body });
/** „Löschen …“: DELETE compta/documents/:id; a paid document needs confirmPaid (its payments go with it). */
export const deleteDocument = (documentId: string, expectedVersion: number, confirmPaid = false) =>
  api<{ id: string; payments: number; allocations: number }>('/compta/documents/' + documentId, { method: 'DELETE', query: { expectedVersion, ...(confirmPaid ? { confirmPaid: 'true' } : {}) } });

/** Inline-disposition signed URL of a document's primary file (use immediately, never cache). */
export const previewUrl = (documentId: string, fileId?: string) =>
  api<{ url: string; expiresIn: number; mimeType: string }>('/compta/documents/' + documentId + '/file', { query: { disposition: 'inline', ...(fileId ? { fileId } : {}) } });

/** LP: „IBAN telefonisch bestätigt“ (right p_mark) – the warning goes, the IBAN becomes known for the supplier. */
export const confirmIban = (documentId: string) => api<{ id: string }>('/compta/documents/' + documentId + '/iban/confirm', { method: 'POST', body: {} });

// ES (02.11.2026): the notes layer of an incoming invoice (escompte box, „Payée“ stamp) – shared/overlay, contract LY.
export interface DocLayer { fileId: string | null; pages: Array<{ w: number; h: number }>; layer: Layer }
export const getLayer = (documentId: string) => api<DocLayer>('/compta/documents/' + documentId + '/layer');
export const moveLayerItem = (documentId: string, item: string, r: { page: number; x: number; y: number; w: number; h: number }) =>
  api<DocLayer>('/compta/documents/' + documentId + '/layer/' + item, { method: 'PUT', body: { page: r.page, x: r.x, y: r.y, w: r.w, h: r.h } });
/** „Drucken“ / „Herunterladen“ of an incoming invoice: a new PDF (original pages unchanged + notes); the original stays as it is. */
export const annotatedPdf = (documentId: string) => apiBytes('/compta/documents/' + documentId + '/annotated', {});
/** Collective printing with notes: ids in order, or the paid invoices of a month in binder order → ZIP of single PDFs. */
export const annotatedZip = (body: { documentIds?: string[]; month?: string; unprinted?: boolean }) => apiBytes('/compta/print/annotated', body);
/** The same to RimoPrint (list „Von RimoCompta“); marked printed once RimoPrint printed them. */
export const annotatedToRimoPrint = (body: { documentIds?: string[]; month?: string; unprinted?: boolean }) =>
  api<{ batchId: string; documents: number; withoutPdf: number }>('/compta/print/annotated', { method: 'POST', body: { ...body, via: 'rimoprint' } });
/** ES correction: the app printed these itself (print dialog) – the paid ones get their „gedruckt“ mark. */
export const markPrinted = (documentIds: string[], via: 'single' | 'zip') => api<{ printed: string[] }>('/compta/print/printed', { method: 'POST', body: { documentIds, via } });
/** Druckübersicht of an accounting month (YYYY-MM). */
export interface PrintOverviewRow { id: string; documentDate: string | null; issuerName: string | null; documentNumber: string | null; totalAmount: string | null; paidAt: string | null; paidAmount: string | null; bank: string | null;
  escompte: boolean; pdf: boolean; printedAt: string | null; printedVia: 'zip' | 'rimoprint' | 'single' | null; printedBy: string | null; filedAt: string | null; filedBy: string | null; waiting: boolean }
export const printOverview = (month: string) => api<{ month: string; unpaid: number; rows: PrintOverviewRow[] }>('/compta/print/overview', { query: { month } });
/** WR: recurring supplier invoices – month view, suggestions and the user's decisions. */
export type Rhythm = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export interface RecurringHint { kind: 'price' | 'duplicate' | 'double_debit'; text: string; textFr: string; pct?: number; documentIds?: string[]; documentId?: string }
export interface RecurringRow { supplierId: string; name: string; rhythm: Rhythm; state: 'received' | 'missing' | 'open'; invoices: Array<{ id: string; month: string; total: number | null; number: string | null; date: string | null }>; hints: RecurringHint[]; text: string | null; textFr: string | null }
export interface RecurringOverview {
  month: string; todayMonth: string; rows: RecurringRow[]; counts: { expected: number; received: number; missing: number; hints: number };
  suggestions: Array<{ supplierId: string; name: string; rhythm: Rhythm; count: number; firstMonth: string; lastMonth: string; lastAmount: number | null; median: number | null }>;
  series: Array<{ supplierId: string; name: string; status: 'confirmed' | 'dismissed'; rhythm: Rhythm; firstMonth: string; decidedBy: string | null; count: number; lastMonth: string | null }>;
}
export const recurring = (month: string) => api<RecurringOverview>('/compta/recurring', { query: { month } });
export const decideRecurring = (supplierId: string, status: 'confirmed' | 'dismissed' | 'reset', rhythm?: Rhythm) =>
  api<{ supplierId: string; status: string | null }>('/compta/recurring/' + supplierId, { method: 'PUT', body: { status, ...(rhythm ? { rhythm } : {}) } });
/** BL2/BL3: the invoice and RimoChantiers' orders (suggestions, assignments with the net comparison). */
export interface OrderLinkView { orderId: string; orderNo: string; project: string | null; orderNet: string; invoiceNet: string; netDiff: string; currency: string; ok: boolean; texts: string[]; textsFr: string[]; linkedBy: string | null; linkedAt: string }
export interface InvoiceOrders {
  net: string | null; linked: OrderLinkView[]; reachable: boolean; reason?: 'no_supplier';
  suggestions: Array<{ orderId: string; orderNo: string; reason: 'number' | 'net'; open: string; diff: string; ok: boolean; text: string; textFr: string }>;
  orders: Array<{ id: string; no: string; date: string | null; project: string | null; netTotal: string; open: string; currency: string }>;
  result?: { orderNo: string; ok: boolean; netDiff: string };
}
export const invoiceOrders = (documentId: string) => api<InvoiceOrders>('/compta/documents/' + documentId + '/orders');
export const linkOrder = (documentId: string, orderId: string) => api<InvoiceOrders>('/compta/documents/' + documentId + '/orders/' + encodeURIComponent(orderId), { method: 'POST', body: {} });
export const unlinkOrder = (documentId: string, orderId: string) => api<InvoiceOrders>('/compta/documents/' + documentId + '/orders/' + encodeURIComponent(orderId), { method: 'DELETE' });
/** „Klassiert“ (in the binder) on/off – paid incoming invoices only. */
export const setFiled = (documentId: string, filed: boolean) => api<{ id: string; filedAt: string | null }>('/compta/documents/' + documentId + '/filed', { method: 'PUT', body: { filed } });

export async function uploadFile(file: File): Promise<{ id: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return api<{ id: string }>('/core/files', { method: 'POST', body: fd });
}

/** Opens the OS file picker for several files. */
export function pickFiles(accept: string): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = true;
    input.onchange = () => resolve([...(input.files || [])]);
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/** Opens the OS file picker and resolves with the chosen file (or null). */
export function pickFile(accept = 'application/pdf,image/jpeg,image/png,image/tiff'): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.oncancel = () => resolve(null);
    input.click();
  });
}

// ---------- imports ----------

export const createImport = (source: 'FILE_UPLOAD' | 'SCAN_FOLDER' | 'WHATSAPP' | 'HERO', destinationType: DocumentTypeContract, mode: 'AI' | 'MANUAL' | 'API') =>
  api<{ id: string }>('/compta/imports', { method: 'POST', body: { source, destinationType, mode } });
export const addImportItem = (jobId: string, fileId: string) =>
  api<{ id: string; status: string }>('/compta/imports/' + jobId + '/items', { method: 'POST', body: { fileId } });
/** 409 duplicate_warning (details.candidates) → confirm again with acceptDuplicate after the user agreed. */
export const confirmImportItem = (itemId: string, fields: Record<string, string | boolean>, acceptDuplicate = false) =>
  api<ApiDocument>('/compta/imports/items/' + itemId + '/confirm', { method: 'POST', body: { fields, ...(acceptDuplicate ? { acceptDuplicate: true } : {}) } });
export const closeImport = (jobId: string) => api('/compta/imports/' + jobId + '/close', { method: 'POST' });
/** "Verwerfen": job CANCELLED, unimported items IGNORED; files and imported documents stay. */
export const discardImport = (jobId: string) => api('/compta/imports/' + jobId + '/discard', { method: 'POST' });

/** Planned values per invoice in a preparation (shown later in the payment queue). */
export const patchPrepItem = (batchId: string, documentId: string, v: { plannedAmount?: string | null; communication?: string | null; plannedBank?: 'BIL' | 'BGL' | null; creditNoteId?: string | null }) =>
  api('/compta/preparation/' + batchId + '/items/' + documentId, { method: 'PATCH', body: v });

// ---------- closing & company ----------

// ---------- statements (Abschluss) ----------
/** CAMT.053 bank statement (+ optional original PDF). Bank, account and month come from the file. */
export async function importStatement(xml: File, pdf?: File | null) {
  const pdfFileId = pdf ? (await uploadFile(pdf)).id : undefined;
  const fd = new FormData();
  fd.append('file', xml);
  if (pdfFileId) fd.append('pdfFileId', pdfFileId);
  return api<{ statementId: string; month: string; bank: string; transactions: number; duplicatesSkipped: number; statements?: Array<{ month: string; bank: string; transactions: number; duplicatesSkipped: number }> }>(
    '/compta/statements/import', { method: 'POST', body: fd });
}
export const setMissingReceipt = (transactionId: string, missing: boolean) =>
  api('/compta/statements/transactions/' + transactionId + '/missing-receipt', { method: 'POST', body: { missing } });
export const manualMatch = (transactionId: string, documentId: string) =>
  api('/compta/statements/transactions/' + transactionId + '/matches', { method: 'POST', body: { documentId } });
/** Original statement PDF + reconciliation pages. */
export async function downloadAnnotated(statementId: string): Promise<string> {
  const r = await api<{ url: string; filename: string }>('/compta/statements/' + statementId + '/annotated');
  await saveUrl(r.url, r.filename);
  return r.filename;
}
export const addClosingItem = (c: ApiClosing, label: string) => api('/compta/closing/' + c.id + '/checklist', { method: 'POST', body: { label } });

export const suggestMatches = (transactionId: string) => api('/compta/statements/transactions/' + transactionId + '/suggestions', { method: 'POST' });
export const decideMatch = (m: { id: string; version: number }, status: 'CONFIRMED' | 'REJECTED') =>
  api('/compta/statements/matches/' + m.id + '/decision', { method: 'POST', body: { status, expectedVersion: m.version } });

export const setChecklist = (c: ApiClosing, itemId: string, status: 'OPEN' | 'DONE' | 'NOT_REQUIRED') =>
  api('/compta/closing/' + c.id + '/checklist/' + itemId, { method: 'POST', body: { status } });
export const closeMonth = (c: ApiClosing) => api<ApiClosing>('/compta/closing/' + c.id + '/close', { method: 'POST', body: { expectedVersion: c.version } });

export const patchCompany = (patch: UpdateCompany) => api<ApiCompany>('/core/company', { method: 'PATCH', body: patch });

// ---------- Pay-Assistant (Socket.IO) ----------
let socket: Socket | null = null;
let offToken: (() => void) | null = null;

/** Socket origin = API origin; namespace /pay-assistant, token in the handshake. */
export function payAssistantStart(onPaidElsewhere: (documentId: string) => void): void {
  if (socket) return;
  const origin = new URL(API_URL).origin;
  socket = io(origin + '/pay-assistant', { auth: (cb) => cb({ token: tokens.access }), transports: ['websocket'] });
  socket.on('connect', () => socket?.emit(EV.START));
  socket.on(EV.MARK_PAID, (b: { documentId: string }) => onPaidElsewhere(b.documentId));
  // RimoID changed the account / ended the session: same as /live (data/liveSync) – refresh now, not on the next call.
  socket.on(LIVE_EVENTS.SESSION, () => void recheckSession());
  socket.on('disconnect', (reason) => { if (reason === 'io server disconnect') void recheckSession(); });
  socket.on('connect_error', (e) => { if (e.message === LIVE_SESSION_INVALID) void recheckSession(); });
  // A reconnect must present a fresh access token: the auth callback reads it on every attempt.
  offToken = tokens.onChange(() => {
    if (socket && !socket.connected) socket.connect();
  });
}

export function payAssistantEmit(event: 'changed' | 'amount' | 'communication', body: Record<string, unknown>): void {
  const name = { changed: EV.CHANGED, amount: EV.AMOUNT_COPIED, communication: EV.COMMUNICATION_COPIED }[event];
  socket?.emit(name, body);
}

export function payAssistantStop(): void {
  socket?.emit(EV.ENDED);
  socket?.disconnect();
  socket = null;
  offToken?.();
  offToken = null;
}

// Printing at the end of the import is gone (ES correction 02.11.2026): invoices are printed after the payment, with their
// notes – Druckübersicht (components/print/PrintOverview.tsx) and the document's own „Drucken“.
